import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import {
  Router,
  ActivatedRoute,
  RouterModule,
  NavigationEnd,
  Event,
} from '@angular/router';
import {
  NavigationItem,
  navigationItemsWithPatient,
} from '../../models/navigation-item-model';
import { classes, pages } from '../../shared/labels';
import { LabelService } from '../../services/label.service';
import { AiSummaryAllComponent } from '../../sections/ai-summary-all/ai-summary-all.component';
import { AiGraphicsComponent } from '../../sections/ai-graphics/ai-graphics.component';
import { AiService } from '../../services/ai.service';
import {
  NuclearTopicsDistrEnriched,
  LastAppointmentSummary,
  PatDmrRelatedData,
  SummaryAll,
} from '../../models/ai-model';
import { Subject, takeUntil, filter } from 'rxjs';
import { ErrorPanelService } from '../../services/error-panel.service';
import { PatientService } from '../../services/patient.service';
import { SidebarService } from '../../services/sidebar.service';
import { AiPatDmrComponent } from '../../sections/ai-patdmr/ai-patdmr.component';
import { MatIconModule } from '@angular/material/icon';
import {
  aiPatient_dynamicMedicalRecord_infoText,
  aiPatient_graph_infoText,
  aiPatient_summaryAll_infoText,
} from '../../models/descriptions/info-texts/info-texts';
import { PdfExportService } from '../../services/pdf-export.service';
import { NuclearTopicsLabelService } from '../../services/label.nucleartopics.service';
import { PatientEssentialInfo } from '../../models/patient-model';

@Component({
  selector: 'app-ai-patient-page',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    RouterModule,
    AiSummaryAllComponent,
    AiGraphicsComponent,
    AiPatDmrComponent,
    MatIconModule,
  ],
  templateUrl: './ai-patient-page.component.html',
  styleUrl: './ai-patient-page.component.scss',
})
export class AiPatientPageComponent implements OnInit, OnDestroy {
  @ViewChild(AiGraphicsComponent) aiGraphicsComponent!: AiGraphicsComponent;
  navigationItems: NavigationItem[] = [];
  patientId: string = '';
  name: string = '';
  patientName: string = '';
  patientSurname: string = '';
  patientNameAndSurname: string = '';
  isPatientActive: boolean = false;
  summaryAllSelected: boolean = false;
  summaryLastAppointmentSelected: boolean = false;
  patDmrSelected: boolean = false;
  graphicsSelected: boolean = false;

  isLoadingPatSumm: boolean = true;
  isLoadingPatDmr: boolean = true;
  isLoadingTopicScores: boolean = true;

  summaryAll: SummaryAll = {
    last_generation_date: '',
    last_generation_time: '',
    html: '',
  };

  patDmr: PatDmrRelatedData = {
    last_generation_date: '',
    last_generation_time: '',
    html: '',
  };

  summaryLastAppointment: LastAppointmentSummary | null = null;
  catDistrPerEachAppointment: NuclearTopicsDistrEnriched[] = [];
  private destroy$ = new Subject<void>();
  showInfoPanel: boolean = false;
  infoTitle: string = aiPatient_summaryAll_infoText.title;
  infoText: string = aiPatient_summaryAll_infoText.text;

  isEditing: boolean = false;
  nuclearTopicsLabels: { [key: string]: string } = {};

  protected colors: string[] = [
    'rgb(112,128,144)',
    'rgb(245, 223, 179)',
    'rgb(245, 235, 179)',
    'rgb(179, 235, 185)',
    'rgb(179, 220, 235)',
    'rgb(235, 179, 235)',
    'rgb(235, 219, 250)',
    'rgb(214, 163, 163)',
    'rgb(150, 120, 220)',
    'rgb(100, 180, 220)',
    'rgb(84, 224, 208)',
  ];

  constructor(
    public labelService: LabelService,
    private aiService: AiService,
    public errorPanelService: ErrorPanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private patientService: PatientService,
    private sidebarService: SidebarService,
    private nuclearTopicsLabelService: NuclearTopicsLabelService,
    private pdfExportService: PdfExportService
  ) {
    this.nuclearTopicsLabels = this.nuclearTopicsLabelService.getTopicLabels();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.patientService
        .getPatientEssentialInfo(params['patientId'])
        .subscribe({
          next: (data: PatientEssentialInfo) => {
            this.patientId = data.id;
            this.patientName = data.first_name;
            this.patientSurname = data.last_name;
            this.isPatientActive = data.is_active;
            this.patientNameAndSurname = `${this.patientName} ${this.patientSurname}`;
            this.navigationItems = this.sidebarService.updateNavigationItems(
              navigationItemsWithPatient,
              this.patientName,
              this.patientSurname,
              this.patientId,
              pages.aiFeatures
            );
            this.getPatSumm();
            this.getPatDmr();
            this.getTopicScoresPerEachAppointment();
            this.updateSelectedSection();
          },
          error: (err) => {
            console.error('Error getting patient essential info:', err);
            this.errorPanelService.showErrorSnackBar(
              'Errore nel recuperare i dati del paziente'
            );
          },
        });
    });

    // Update selected section based on the current route
    this.router.events
      .pipe(
        filter(
          (event: Event): event is NavigationEnd =>
            event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateSelectedSection();
      });

    // Initial update of selected section
    this.updateSelectedSection();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPatSumm() {
    this.isLoadingPatSumm = true;
    this.aiService.getPatSumm(this.patientId).subscribe({
      next: (response) => {
        this.summaryAll = response;
        this.isLoadingPatSumm = false;
      },
      error: (err) => {
        console.error('Errore nel recuperare riassunto del paziente', err);
        this.isLoadingPatSumm = false;
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare il riassunto del paziente'
        );
      },
    });
  }
  getPatDmr() {
    this.isLoadingPatDmr = true;
    this.aiService.getPatDmr(this.patientId).subscribe({
      next: (response: PatDmrRelatedData | null) => {
        if (response) {
          this.patDmr = response;
        } else {
          console.log('No patdmr found for the patient');
          this.patDmr = {
            last_generation_date: '',
            last_generation_time: '',
            html: '',
          };
        }
        this.isLoadingPatDmr = false;
      },
      error: (err) => {
        console.error(
          'Errore nel recuperare il riassunto ultimo appuntamento',
          err
        );
        this.errorPanelService.showErrorSnackBar(
          "Errore nel recuperare il riassunto dell'ultimo appuntamento"
        );
        this.isLoadingPatDmr = false;
      },
    });
  }

  getTopicScoresPerEachAppointment() {
    this.isLoadingTopicScores = true;
    this.aiService.getGraphicsData(this.patientId).subscribe({
      next: (response) => {
        this.catDistrPerEachAppointment = response;
        this.isLoadingTopicScores = false;
      },
      error: (err) => {
        console.error('Errore nel recuperare i dati per grafici', err);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i dati per i grafici'
        );
        this.isLoadingTopicScores = false;
      },
    });
  }

  updateSelectedSection() {
    const currentUrl = this.router.url;
    if (currentUrl.includes('sintesi-complessiva')) {
      this.infoTitle = aiPatient_summaryAll_infoText.title;
      this.infoText = aiPatient_summaryAll_infoText.text;
    }
    this.summaryAllSelected = currentUrl.includes('sintesi-complessiva');
    this.summaryLastAppointmentSelected = currentUrl.includes(
      'summary-last-appointment'
    ); // TODO: should we remove this?
    if (currentUrl.includes('scheda-clinica-dinamica')) {
      this.infoTitle = aiPatient_dynamicMedicalRecord_infoText.title;
      this.infoText = aiPatient_dynamicMedicalRecord_infoText.text;
    }
    this.patDmrSelected = currentUrl.includes('scheda-clinica-dinamica');
    if (currentUrl.includes('grafico-topic')) {
      this.infoTitle = aiPatient_graph_infoText.title;
      this.infoText = aiPatient_graph_infoText.text;
    }
    this.graphicsSelected = currentUrl.includes('grafico-topic');
    this.changeDetectorRef.detectChanges(); // Add this line to trigger change detection
  }

  navigateTo(route: string) {
    this.router.navigate([route], { relativeTo: this.route });
    this.updateSelectedSection(); // Add this line to update the selected section after navigation
  }

  navigateToAiPage() {
    this.router.navigate([`/analisi-ai/${this.patientId}`], {
      state: {
        patientId: sessionStorage.getItem('patientId'),
        first_name: sessionStorage.getItem('patientName'),
        last_name: sessionStorage.getItem('patientSurname'),
      },
    });
  }

  toggleInfoPanel() {
    this.showInfoPanel = !this.showInfoPanel;
  }

  downloadPDF() {
    if (typeof window === 'undefined') return;

    this.pdfExportService.downloadPDF({
      filename: this.patientNameAndSurname,
      isGraphics: this.graphicsSelected,
      isSummary: this.summaryAllSelected,
      element: document.querySelector('.table-container') as HTMLElement,
      graphicsComponent: this.aiGraphicsComponent,
    });
  }

  toggleEdit(): void {
    this.isEditing = !this.isEditing;
  }
}
